import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  type Branch,
  type LegalEntity,
  type Role,
  type ScopeType,
  type User,
  type AccountKind,
} from '../api.ts';
import { can, useMe } from '../auth.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Textarea,
} from '../components/ui.tsx';
import { changed, FormModal, FormSection, ReasonDialog } from '../components/FormModal.tsx';
import {
  ListToolbar,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../components/ListFilters.tsx';
import { matchesUser, NO_ROLE, roleOptions } from './users-filters.ts';
import { ConfirmDialog } from '../components/Modal.tsx';
import { MasterBadge } from '../components/MasterBadge.tsx';
import { TierBadge } from './Roles.tsx';
import { localized } from '../i18n/index.ts';
import { permissionDefinition } from '../permission-areas.ts';
import { groupOnlyConflict } from '@loot/shared';

type Form = {
  email: string;
  displayName: string;
  locale: 'pt-BR' | 'en';
  timezone: string;
  isActive: boolean;
  accountKind: AccountKind;
};
const EMPTY: Form = {
  email: '',
  displayName: '',
  locale: 'pt-BR',
  timezone: 'America/Sao_Paulo',
  isActive: true,
  accountKind: 'person',
};
const KINDS: AccountKind[] = ['person', 'generic', 'technical'];

function useDirectory() {
  const entities = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });
  const branches = useQuery({
    queryKey: ['branches', ''],
    queryFn: () => api.get<Branch[]>('/core/branches'),
  });
  return {
    entities: entities.data ?? [],
    branches: branches.data ?? [],
    entityById: new Map(entities.data?.map((e) => [e.id, e])),
    branchById: new Map(branches.data?.map((b) => [b.id, b])),
  };
}

/** Cadastro e edição da pessoa, em janela por cima da lista (guia visual, FormModal). */
function UserForm({ user, onDone }: { user: User | null; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [initial] = useState<Form>(() =>
    user
      ? {
          email: user.email,
          displayName: user.displayName,
          locale: user.locale,
          timezone: user.timezone,
          isActive: user.isActive,
          accountKind: user.accountKind,
        }
      : EMPTY,
  );
  const [form, setForm] = useState<Form>(initial);
  // Tipo da conta (ADR 0042): "a classificar" não se escolhe; genérica e técnica não voltam a pessoa.
  const kinds = KINDS.filter(
    (k) => !user || k === user.accountKind || user.accountKind === 'unclassified' || k !== 'person',
  );
  const save = useMutation({
    mutationFn: () => {
      const { accountKind, ...rest } = form;
      const body =
        accountKind === 'unclassified' || (user && accountKind === user.accountKind)
          ? rest
          : { ...rest, accountKind };
      return user
        ? api.put<User>(`/iam/users/${user.id}`, body)
        : api.post<User>('/iam/users', body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
  });

  return (
    <FormModal
      open
      title={user ? t('users.edit') : t('users.new')}
      description={user ? user.displayName : t('users.inviteNote')}
      saveLabel={user ? undefined : t('users.new')}
      dirty={changed(initial, form)}
      saving={save.isPending}
      error={save.error}
      onSave={() => save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('users.email')} hint={user ? t('users.emailChangeHint') : undefined}>
          <Input
            id="user-email"
            type="email"
            required
            value={form.email}
            onChange={(ev) => setForm({ ...form, email: ev.target.value })}
          />
        </Field>
        <Field label={t('fields.name')}>
          <Input
            id="user-name"
            required
            value={form.displayName}
            onChange={(ev) => setForm({ ...form, displayName: ev.target.value })}
          />
        </Field>
        <Field label={t('fields.language')}>
          <Select
            id="user-locale"
            value={form.locale}
            onChange={(ev) => setForm({ ...form, locale: ev.target.value as Form['locale'] })}
          >
            <option value="pt-BR">Português</option>
            <option value="en">English</option>
          </Select>
        </Field>
        <Field label={t('fields.timezone')} hint={t('fields.timezoneHint')}>
          <Input
            id="user-timezone"
            required
            value={form.timezone}
            onChange={(ev) => setForm({ ...form, timezone: ev.target.value })}
          />
        </Field>
        <Field label={t('users.accountKind')} hint={t('users.accountKindHint')}>
          <Select
            id="user-account-kind"
            value={form.accountKind}
            onChange={(ev) => setForm({ ...form, accountKind: ev.target.value as AccountKind })}
          >
            {form.accountKind === 'unclassified' && (
              <option value="unclassified">{t('users.kinds.unclassified')}</option>
            )}
            {kinds.map((k) => (
              <option key={k} value={k}>
                {t(`users.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 self-end text-sm">
          <input
            id="user-active"
            type="checkbox"
            checked={form.isActive}
            onChange={(ev) => setForm({ ...form, isActive: ev.target.checked })}
          />
          {t('fields.active')}
        </label>
        {user && !form.isActive && user.isActive && (
          <p className="text-sm text-danger sm:col-span-2">{t('users.deactivateWarning')}</p>
        )}
      </FormSection>
    </FormModal>
  );
}

/** Dá um perfil para a pessoa, valendo no grupo todo, em uma empresa ou em uma filial. */
function AssignForm({ user, onDone }: { user: User; onDone: () => void }) {
  // Fase de desenvolvimento (ADR 0053): o master aprova também os próprios pedidos.
  const devPhase = !!useMe().data?.master?.exceptionUntil;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const dir = useDirectory();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<Role[]>('/iam/roles') });
  const [initial] = useState({
    roleId: '',
    scopeType: 'legal_entity' as ScopeType,
    legalEntityId: '',
    branchId: '',
    justification: '',
    acknowledgeGroupOnly: false,
  });
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);

  const activeRoles = roles.data?.filter((r) => r.isActive) ?? [];
  const chosen = activeRoles.find((r) => r.id === form.roleId);
  // ADR 0075: perfil normal é concedido na hora; os demais pedem motivo. O sensível o Administrador
  // concede direto, e o master concede qualquer um; nos outros casos vira pedido aprovado por outra
  // pessoa (o servidor decide e a tela segue a resposta).
  const needsApproval = !!chosen && chosen.tier !== 'normal';
  // Auditoria de 27/10: permissões "só do grupo" não valem num perfil dado por empresa ou filial.
  const groupOnly = chosen ? groupOnlyConflict(chosen.permissions, form.scopeType) : null;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        userId: user.id,
        roleId: form.roleId,
        scopeType: form.scopeType,
        legalEntityId: form.scopeType === 'legal_entity' ? form.legalEntityId : null,
        branchId: form.scopeType === 'branch' ? form.branchId : null,
        acknowledgeGroupOnly: form.acknowledgeGroupOnly,
      };
      if (!needsApproval) return api.post('/iam/role-assignments', body).then(() => 'granted');
      const withReason = { ...body, justification: form.justification };
      return api.post('/iam/role-assignments', withReason).then(
        () => 'granted',
        (err: unknown) => {
          if ((err as { code?: string }).code !== 'approval_required') throw err;
          return api.post('/iam/access-requests', withReason).then(() => 'requested');
        },
      );
    },
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      await qc.invalidateQueries({ queryKey: ['access-requests'] });
      if (result === 'requested') setSent(true);
      else onDone();
    },
  });

  const title = t('users.assignTitle', { name: user.displayName });
  if (sent) {
    return (
      <FormModal
        open
        readOnly
        size="md"
        title={title}
        dirty={false}
        onSave={onDone}
        onClose={onDone}
      >
        <p role="status" className="text-sm">
          {t('access.sent')}
        </p>
      </FormModal>
    );
  }

  return (
    <FormModal
      open
      title={title}
      description={t('users.assignNote')}
      saveLabel={needsApproval ? t('access.request') : t('users.assign')}
      dirty={changed(initial, form)}
      saving={save.isPending}
      error={save.error ?? (roles.isError ? roles.error : undefined)}
      // Permissão "só do grupo" num perfil que não vale fora dele: o aviso explica e nada é salvo.
      onSave={() => !groupOnly?.useless && save.mutate()}
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('users.role')}>
          <Select
            id="assign-role"
            required
            value={form.roleId}
            onChange={(ev) => setForm({ ...form, roleId: ev.target.value })}
          >
            <option value="" disabled>
              {t('common.choose')}
            </option>
            {activeRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {localized(r.name)} ({r.code}) · {t(`access.tiers.${r.tier}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('users.scope')}>
          <Select
            id="assign-scope"
            value={form.scopeType}
            onChange={(ev) => setForm({ ...form, scopeType: ev.target.value as ScopeType })}
          >
            {(['group', 'legal_entity', 'branch'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`scope.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        {form.scopeType === 'legal_entity' && (
          <Field label={t('branches.legalEntity')}>
            <Select
              id="assign-entity"
              required
              value={form.legalEntityId}
              onChange={(ev) => setForm({ ...form, legalEntityId: ev.target.value })}
            >
              <option value="" disabled>
                {t('common.choose')}
              </option>
              {dir.entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code} · {e.legalName}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {form.scopeType === 'branch' && (
          <Field label={t('users.branch')}>
            <Select
              id="assign-branch"
              required
              value={form.branchId}
              onChange={(ev) => setForm({ ...form, branchId: ev.target.value })}
            >
              <option value="" disabled>
                {t('common.choose')}
              </option>
              {dir.branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {dir.entityById.get(b.legalEntityId)?.code ?? '?'} / {b.code} · {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {form.scopeType === 'group' && (
          <p className="text-sm text-muted sm:col-span-2">{t('users.groupScopeWarning')}</p>
        )}
        {chosen && (
          <p className="text-sm text-muted sm:col-span-2">
            <TierBadge tier={chosen.tier} /> {t(`access.tierHelp.${chosen.tier}`)}
            {devPhase && chosen.tier !== 'normal' && <> {t('access.devPhaseNote')}</>}
          </p>
        )}
        {groupOnly && (
          <div className="flex flex-col gap-2 text-sm sm:col-span-2">
            <p className={groupOnly.useless ? 'text-danger' : 'text-brand-text'}>
              {t(groupOnly.useless ? 'users.groupOnlyUseless' : 'users.groupOnlyWarning')}{' '}
              {groupOnly.groupOnly
                .map((c) => {
                  const def = permissionDefinition(c);
                  return def ? localized(def.label) : c;
                })
                .join('; ')}
            </p>
            {!groupOnly.useless && (
              <label className="flex items-center gap-2">
                <input
                  id="assign-group-only-ack"
                  type="checkbox"
                  required
                  checked={form.acknowledgeGroupOnly}
                  onChange={(ev) => setForm({ ...form, acknowledgeGroupOnly: ev.target.checked })}
                />
                {t('users.groupOnlyAck')}
              </label>
            )}
          </div>
        )}
        {needsApproval && (
          <div className="sm:col-span-2">
            <Field label={t('access.justification')} hint={t('access.justificationHint')}>
              <Textarea
                id="assign-justification"
                required
                minLength={10}
                maxLength={1000}
                value={form.justification}
                onChange={(ev) => setForm({ ...form, justification: ev.target.value })}
              />
            </Field>
          </div>
        )}
      </FormSection>
    </FormModal>
  );
}

type UserDialog =
  | { kind: 'revoke'; assignmentId: string; role: string; name: string }
  | { kind: 'revokeCritical'; assignmentId: string; role: string }
  | { kind: 'block' | 'reclassify' | 'unblock'; user: User };

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  // Fase de desenvolvimento (ADR 0053): o master também altera os próprios acessos (fica registrado).
  const selfLocked = (id: string) => id === me?.user.id && !me?.master?.exceptionUntil;
  const qc = useQueryClient();
  const canManage = can(me, 'iam.user.manage');
  const canGrant = can(me, 'iam.access.grant');
  const dir = useDirectory();
  const [open, setOpen] = useState<
    { kind: 'user'; user: User | null } | { kind: 'assign'; user: User } | null
  >(null);

  // Confirmações e pedidos de motivo abrem em janela (guia visual, Modal), nunca no navegador.
  const [dialog, setDialog] = useState<UserDialog | null>(null);
  const [sent, setSent] = useState(false);
  const requestSent = () => {
    setDialog(null);
    setSent(true);
  };

  const list = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/iam/users') });
  // Busca e filtros (guia visual): nome ou e-mail, situação, perfil e tipo da conta.
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const [roleFilter, setRoleFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const roles = roleOptions(list.data ?? [], localized, i18n.language);
  const rows = (list.data ?? []).filter((u) =>
    matchesUser(u, { q, status, roleId: roleFilter, kind: kindFilter }),
  );
  const unassign = useMutation({
    mutationFn: (id: string) => api.delete(`/iam/role-assignments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
  // Bloqueio emergencial (incidente) e pedido de desbloqueio, aprovado por outra pessoa.
  const block = useMutation({
    mutationFn: (v: { id: string; justification: string }) =>
      api.post(`/iam/users/${v.id}/emergency-block`, { justification: v.justification }),
    onSuccess: async () => {
      setDialog(null);
      await qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
  const askReclassify = useMutation({
    mutationFn: (v: { userId: string; justification: string }) =>
      api.post('/iam/access-requests/reclassify', v),
    onSuccess: async () => {
      requestSent();
      await qc.invalidateQueries({ queryKey: ['access-requests'] });
    },
  });
  const askUnblock = useMutation({
    mutationFn: (v: { userId: string; justification: string }) =>
      api.post('/iam/access-requests/unblock', v),
    onSuccess: async () => {
      requestSent();
      await qc.invalidateQueries({ queryKey: ['access-requests'] });
    },
  });
  // Perfil crítico só sai por pedido aprovado por outra pessoa (ADR 0042).
  const askRevoke = useMutation({
    mutationFn: (v: { assignmentId: string; justification: string }) =>
      api.post('/iam/access-requests/revoke', v),
    onSuccess: async () => {
      requestSent();
      await qc.invalidateQueries({ queryKey: ['access-requests'] });
    },
  });

  const closeDialog = () => {
    setDialog(null);
    block.reset();
    askReclassify.reset();
    askUnblock.reset();
    askRevoke.reset();
  };
  // Pedido de motivo aberto: o texto e a ação de cada caso.
  const reason = (() => {
    if (!dialog || dialog.kind === 'revoke') return null;
    if (dialog.kind === 'revokeCritical')
      return {
        title: t('users.removeRole'),
        description: t('access.revokeText', { role: dialog.role }),
        fieldLabel: t('common.reason'),
        confirmLabel: t('access.sendRequest'),
        tone: 'primary' as const,
        mutation: askRevoke,
        run: (justification: string) =>
          askRevoke.mutate({ assignmentId: dialog.assignmentId, justification }),
      };
    const name = dialog.user.displayName;
    const id = dialog.user.id;
    if (dialog.kind === 'block')
      return {
        title: t('users.block'),
        description: t('users.blockText', { name }),
        fieldLabel: t('users.blockField'),
        confirmLabel: t('users.blockConfirm'),
        tone: 'danger' as const,
        mutation: block,
        run: (justification: string) => block.mutate({ id, justification }),
      };
    if (dialog.kind === 'reclassify')
      return {
        title: t('users.reclassify'),
        description: t('users.reclassifyText', { name }),
        fieldLabel: t('users.reclassifyField'),
        confirmLabel: t('access.sendRequest'),
        tone: 'primary' as const,
        mutation: askReclassify,
        run: (justification: string) => askReclassify.mutate({ userId: id, justification }),
      };
    return {
      title: t('users.unblock'),
      description: t('users.unblockText', { name }),
      fieldLabel: t('common.reason'),
      confirmLabel: t('access.sendRequest'),
      tone: 'primary' as const,
      mutation: askUnblock,
      run: (justification: string) => askUnblock.mutate({ userId: id, justification }),
    };
  })();

  const scopeLabel = (a: User['assignments'][number]) => {
    if (a.scopeType === 'group') return t('scope.group');
    if (a.scopeType === 'legal_entity')
      return dir.entityById.get(a.legalEntityId ?? '')?.code ?? t('scope.legal_entity');
    const b = dir.branchById.get(a.branchId ?? '');
    return b
      ? `${dir.entityById.get(b.legalEntityId)?.code ?? '?'} / ${b.code}`
      : t('scope.branch');
  };
  const dateTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.description')}
        actions={
          canManage ? (
            <Button onClick={() => setOpen({ kind: 'user', user: null })}>{t('users.new')}</Button>
          ) : null
        }
      />

      {open?.kind === 'user' && (
        <UserForm key={open.user?.id ?? 'new'} user={open.user} onDone={() => setOpen(null)} />
      )}
      {open?.kind === 'assign' && (
        <AssignForm key={open.user.id} user={open.user} onDone={() => setOpen(null)} />
      )}
      {sent && (
        <p role="status" className="rounded-lg bg-ok/10 p-3 text-sm text-ok">
          {t('access.sent')}
        </p>
      )}
      {/* Com a janela aberta, o erro do pedido aparece nela. */}
      <ErrorBox
        error={
          unassign.error ??
          (dialog
            ? null
            : (askRevoke.error ?? block.error ?? askUnblock.error ?? askReclassify.error))
        }
      />
      <ConfirmDialog
        open={dialog?.kind === 'revoke'}
        title={t('users.removeRole')}
        description={
          dialog?.kind === 'revoke'
            ? t('users.removeRoleConfirm', { role: dialog.role, name: dialog.name })
            : undefined
        }
        confirmLabel={t('users.removeRole')}
        tone="danger"
        busy={unassign.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          if (dialog?.kind === 'revoke') unassign.mutate(dialog.assignmentId);
          setDialog(null);
        }}
      />
      <ReasonDialog
        open={reason !== null}
        title={reason?.title}
        description={reason?.description}
        fieldLabel={reason?.fieldLabel ?? ''}
        confirmLabel={reason?.confirmLabel ?? ''}
        tone={reason?.tone}
        pending={reason?.mutation.isPending}
        error={reason?.mutation.error}
        onConfirm={(justification) => reason?.run(justification)}
        onClose={closeDialog}
      />

      <ListToolbar>
        <SearchBox value={q} onChange={setQ} placeholder={t('users.search')} />
        <StatusFilter value={status} onChange={setStatus} />
        <Select
          id="users-role-filter"
          className="w-full sm:w-64"
          aria-label={t('users.filterByRole')}
          value={roleFilter}
          onChange={(ev) => setRoleFilter(ev.target.value)}
        >
          <option value="">{t('users.allRoles')}</option>
          <option value={NO_ROLE}>{t('users.withoutRole')}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <Select
          id="users-kind-filter"
          className="w-full sm:w-52"
          aria-label={t('users.filterByKind')}
          value={kindFilter}
          onChange={(ev) => setKindFilter(ev.target.value)}
        >
          <option value="">{t('users.allKinds')}</option>
          {(['person', 'generic', 'technical', 'unclassified'] as const).map((k) => (
            <option key={k} value={k}>
              {t(`users.kinds.${k}`)}
            </option>
          ))}
        </Select>
      </ListToolbar>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          head={[t('fields.name'), t('users.roles'), t('users.lastLogin'), t('fields.status'), '']}
        >
          {rows.length === 0 && (
            <EmptyRow colSpan={5}>
              {list.data.length === 0 ? t('users.empty') : t('users.noMatch')}
            </EmptyRow>
          )}
          {rows.map((u) => (
            <tr key={u.id}>
              <td className="min-w-52">
                {u.displayName}
                <div className="break-all text-xs text-muted">{u.email}</div>
              </td>
              <td>
                {/* A marca de master não é perfil gravado (ADR 0051): sem a etiqueta, a linha
                    pareceria sem acesso nenhum. */}
                {u.isMaster && (
                  <div className="mb-1 flex flex-col gap-1">
                    <MasterBadge />
                    <span className="text-xs text-muted">{t('users.masterHelp')}</span>
                  </div>
                )}
                {u.assignments.length === 0 && !u.isMaster && (
                  <span className="text-xs text-muted">{t('users.noRoles')}</span>
                )}
                <ul className="flex flex-col gap-1">
                  {u.assignments.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="accent">{localized(a.roleName)}</Badge>
                      <span className="text-muted">{scopeLabel(a)}</span>
                      {a.singleHolderReleaseId && (
                        <>
                          <Badge tone={a.inForce ? 'info' : 'off'}>{t('singleHolder.seal')}</Badge>
                          <span className="text-muted">
                            {a.inForce && a.expiresAt
                              ? t('singleHolder.until', {
                                  date: dateTime.format(new Date(a.expiresAt)),
                                })
                              : t('singleHolder.noEffect')}
                          </span>
                        </>
                      )}
                      {canGrant && !selfLocked(u.id) && (
                        <button
                          type="button"
                          className="text-danger hover:underline disabled:opacity-50"
                          disabled={unassign.isPending || askRevoke.isPending}
                          aria-label={t('users.removeRole')}
                          onClick={() =>
                            setDialog(
                              a.tier === 'critical'
                                ? {
                                    kind: 'revokeCritical',
                                    assignmentId: a.id,
                                    role: localized(a.roleName),
                                  }
                                : {
                                    kind: 'revoke',
                                    assignmentId: a.id,
                                    role: localized(a.roleName),
                                    name: u.displayName,
                                  },
                            )
                          }
                        >
                          {t('users.removeRole')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </td>
              <td className="text-xs">
                {u.lastLoginAt
                  ? dateTime.format(new Date(u.lastLoginAt))
                  : t('users.neverLoggedIn')}
                {!u.linked && <div className="text-muted">{t('users.notLinked')}</div>}
              </td>
              <td>
                {u.blockedAt ? (
                  <Badge tone="off">{t('users.blocked')}</Badge>
                ) : u.isActive ? (
                  <Badge tone="ok">{t('fields.active')}</Badge>
                ) : (
                  <Badge tone="off">{t('fields.inactive')}</Badge>
                )}
                {u.accountKind !== 'person' && (
                  <div className="mt-1">
                    <Badge tone="info">{t(`users.kinds.${u.accountKind}`)}</Badge>
                  </div>
                )}
                {u.blockReason && <div className="mt-1 text-xs text-muted">{u.blockReason}</div>}
              </td>
              <td className="text-right">
                {/* Ninguém mexe nos próprios acessos (ADR 0042), salvo o master na fase de desenvolvimento. */}
                {selfLocked(u.id) ? (
                  <span className="text-xs text-muted">{t('access.selfNote')}</span>
                ) : (
                  <div className="flex justify-end gap-2">
                    {canGrant && (
                      <Button
                        variant="secondary"
                        onClick={() => setOpen({ kind: 'assign', user: u })}
                      >
                        {t('users.assign')}
                      </Button>
                    )}
                    {canManage && (
                      <Button
                        variant="secondary"
                        onClick={() => setOpen({ kind: 'user', user: u })}
                      >
                        {t('common.edit')}
                      </Button>
                    )}
                    {/* Ninguém bloqueia a própria conta, nem o master. */}
                    {canManage && !u.blockedAt && u.id !== me?.user.id && (
                      <Button
                        variant="secondary"
                        disabled={block.isPending}
                        onClick={() => setDialog({ kind: 'block', user: u })}
                      >
                        {t('users.block')}
                      </Button>
                    )}
                    {canGrant && (u.accountKind === 'generic' || u.accountKind === 'technical') && (
                      <Button
                        variant="secondary"
                        disabled={askReclassify.isPending}
                        onClick={() => setDialog({ kind: 'reclassify', user: u })}
                      >
                        {t('users.reclassify')}
                      </Button>
                    )}
                    {canGrant && u.blockedAt && (
                      <Button
                        variant="secondary"
                        disabled={askUnblock.isPending}
                        onClick={() => setDialog({ kind: 'unblock', user: u })}
                      >
                        {t('users.unblock')}
                      </Button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
